import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type DeviceEventView, type DeviceView, type DevicePairingCodeView } from '@vello/api-client';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, ListTable, Measure, Notice, PageHeader, Panel, Row, SectionTitle, Select, Stack, Text, TextInput, type Column, type TimelineEntry, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  DEVICE_ACTOR,
  DEVICE_REVOKE_REASON_CHOICES,
  REGISTERABLE_DEVICE_KINDS,
  adminScreenMarket,
  adminScreenMarketChoices,
  deviceActions,
  deviceAuthority,
  deviceCounts,
  deviceStateTone,
  eventReasonKind,
  formatMarketInstant,
  marketTimeZone,
  operatorLabel,
  sortDeviceEvents,
  sortDevices,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { DrawerStack } from '../components/drawer-stack';
import { ScopeMarketNotice } from '../components/scope-market';
import { HistoryTimeline } from '../components/history-timeline';

/**
 * Operações › Aparelhos (DEVICE-PAIRING, D561, ADR-0116): the market's registered devices - the operations-room TV panel and the lobby kiosk.
 * An operator registers one, generates its single-use pairing code (shown ONCE, with when it lapses in the market's zone) and the person at the
 * device types it; the device then holds its own permanent token, scoped to its type and this market. An operator revokes a device with a reason
 * from a closed list: it stops working at once and the registration is terminal. Every step is in the device's history. The token is never shown
 * here - the server keeps only its hash. Layout and type come from the web-theme vocabulary (no inline style). Neutral: no country, market name
 * or language is named here.
 */

type Notice_ = { readonly tone: 'success' | 'danger'; readonly text: string };

export function DevicesScreen(): ReactElement {
  const { runtime, scope, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, reason, explain, fail } = useVocab();
  const authority = deviceAuthority(grants);
  const { navigate } = useRouter();
  const { confirm } = useConsole();

  const choices = useMemo(() => adminScreenMarketChoices(scope, markets), [scope, markets]);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const market = adminScreenMarket(scope, choices, picked);
  const showPicker = scope.current.city === undefined && choices.length > 1;
  const timeZone = marketTimeZone(market, markets);

  const [devices, setDevices] = useState<readonly DeviceView[] | undefined>(undefined);
  const [operators, setOperators] = useState<Readonly<Record<string, string>>>({});
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<Notice_ | undefined>(undefined);
  const [kind, setKind] = useState('');
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<DevicePairingCodeView | undefined>(undefined);
  const [revoking, setRevoking] = useState<DeviceView | undefined>(undefined);
  const [revokeReason, setRevokeReason] = useState('');
  const [history, setHistory] = useState<{ readonly device: DeviceView; readonly events: readonly DeviceEventView[]; readonly operators: Readonly<Record<string, string>> } | undefined>(undefined);
  const [historyError, setHistoryError] = useState(false);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (e: { readonly code: string; readonly reasonCode?: string | undefined; readonly params?: Readonly<Record<string, string | number>> | undefined }): string =>
      explain(e) ?? t('lmap.err.generic', { code: fail(e) }),
    [explain, fail, t],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setDevices(undefined);
      setError(undefined);
      return;
    }
    const res = await runtime.ops.devices(market);
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setDevices(res.value.devices);
      setOperators(res.value.operators);
      setError(undefined);
    } else {
      // A refused read keeps the list unknown: the screen says it could not load, never "no devices".
      setError(refuse(res.error));
    }
  }, [runtime, market, refuse]);

  useEffect(() => {
    setDevices(undefined);
    setIssued(undefined);
    setRevoking(undefined);
    setHistory(undefined);
    setNotice(undefined);
    void load();
  }, [load]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== undefined });

  const rows = useMemo(() => sortDevices(devices ?? []), [devices]);
  const counts = useMemo(() => deviceCounts(devices ?? []), [devices]);
  const instant = (value: string | undefined): string => formatMarketInstant(value, timeZone, lang);

  const register = async (): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.registerDevice(market, { kind, label });
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('devices.done.registered') });
      setKind('');
      setLabel('');
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  const issue = async (device: DeviceView): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    setNotice(undefined);
    setRevoking(undefined);
    const res = await runtime.ops.issueDevicePairingCode(market, device.deviceId);
    setBusy(false);
    if (res.ok) setIssued(res.value);
    else {
      setIssued(undefined);
      setNotice({ tone: 'danger', text: refuse(res.error) });
    }
    await load();
  };

  const revoke = async (device: DeviceView): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.revokeDevice(market, device.deviceId, revokeReason);
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('devices.done.revoked') });
      setRevoking(undefined);
      setHistory(undefined); // the device drawer closes; the confirmation shows on the page
      setRevokeReason('');
      if (issued?.device.deviceId === device.deviceId) setIssued(undefined);
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  /** "Excluir aparelho": a terminal soft delete of a REVOKED device behind a danger confirmation; its history stays on record. */
  const remove = async (device: DeviceView): Promise<void> => {
    if (market === undefined) return;
    const answer = await confirm({
      title: t('devices.delete.confirmTitle', { value: device.label }),
      body: t('devices.delete.body'),
      confirmLabel: t('devices.delete.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: 'danger',
    });
    if (!answer.confirmed) return;
    setBusy(true);
    const res = await runtime.ops.deleteDevice(market, device.deviceId);
    setBusy(false);
    if (res.ok) {
      setHistory(undefined); // the drawer closes; the confirmation shows on the page
      setRevoking(undefined);
      setRevokeReason('');
      setNotice({ tone: 'success', text: t('devices.done.deleted') });
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  const openHistory = async (device: DeviceView): Promise<void> => {
    if (market === undefined) return;
    setHistoryError(false);
    const res = await runtime.ops.deviceEvents(market, device.deviceId);
    if (res.ok) setHistory({ device: res.value.device, events: sortDeviceEvents(res.value.events), operators: res.value.operators });
    else {
      setHistory({ device, events: [], operators: {} });
      setHistoryError(true);
    }
  };

  const actorText = (actor: string, names: Readonly<Record<string, string>>): string => (actor === DEVICE_ACTOR ? t('devices.actor.device') : operatorLabel(names, actor));

  const eventDetail = (e: DeviceEventView): string => {
    const kindOfReason = eventReasonKind(e);
    if (kindOfReason === 'revoke_reason') return v('device.revoke_reason', e.reasonCode);
    if (kindOfReason === 'refusal') return reason(e.reasonCode);
    if (e.type === 'code_issued' && typeof e.detail.expiresAt === 'string') return t('devices.history.expires', { value: instant(e.detail.expiresAt) });
    if (e.type === 'registered' && typeof e.detail.kind === 'string') return v('device.kind', e.detail.kind);
    return '—';
  };

  const columns: readonly Column<DeviceView>[] = [
    {
      key: 'device',
      header: t('devices.col.device'),
      width: 'minmax(220px, 1.5fr)',
      render: (d) => (
        <Stack gap={1}>
          <Text as="strong">{d.label}</Text>
          <Text role="caption" tone="secondary">{v('device.kind', d.kind)}</Text>
        </Stack>
      ),
    },
    {
      key: 'state',
      header: t('devices.col.state'),
      width: 'minmax(170px, 1fr)',
      render: (d) => <Badge tone={deviceStateTone(d.state)}>{v('device.state', d.state)}</Badge>,
    },
    {
      key: 'activity',
      header: t('devices.col.activity'),
      width: 'minmax(220px, 1.3fr)',
      render: (d) => (
        <Stack gap={1}>
          <Text role="caption" tone="secondary" className="tnum">{d.pairedAt !== undefined ? t('devices.pairedAt', { value: instant(d.pairedAt) }) : t('devices.neverPaired')}</Text>
          {d.state === 'paired' && <Text role="caption" tone="secondary" className="tnum">{d.lastSeenAt !== undefined ? t('devices.seenAt', { value: instant(d.lastSeenAt) }) : t('devices.neverSeen')}</Text>}
          {d.revokedAt !== undefined && <Text role="caption" tone="secondary" className="tnum">{t('devices.revokedAt', { value: instant(d.revokedAt) })}{d.revokeReason !== undefined ? ` · ${v('device.revoke_reason', d.revokeReason)}` : ''}</Text>}
        </Stack>
      ),
    },
    {
      key: 'registered',
      header: t('devices.col.registered'),
      width: 'minmax(200px, 1.2fr)',
      render: (d) => (
        <Stack gap={1}>
          <Text role="caption" tone="secondary" className="tnum">{instant(d.registeredAt)}</Text>
          <Text role="caption" tone="secondary">{t('devices.registeredBy', { value: operatorLabel(operators, d.registeredBy) })}</Text>
        </Stack>
      ),
    },
  ];

  /** The device's history as a timeline: a revocation is red, a refused pairing is amber, the rest neutral. */
  const historyEntries = (h: { readonly events: readonly DeviceEventView[]; readonly operators: Readonly<Record<string, string>> }): readonly TimelineEntry[] =>
    h.events.map((e) => {
      const detail = eventDetail(e);
      return {
        id: e.eventId,
        tone: e.type === 'revoked' ? 'danger' : e.type === 'pairing_refused' ? 'warning' : 'neutral',
        title: v('device.event_type', e.type),
        ...(detail !== '—' ? { detail } : {}),
        meta: `${instant(e.occurredAt)} · ${actorText(e.actor, h.operators)}`,
      };
    });

  /** The revoke confirmation lives in the device's drawer (reason from the closed list + the warning), never below the list. */
  const startRevoke = (device: DeviceView): void => {
    setRevoking(device);
    setRevokeReason('');
    setNotice(undefined);
    void openHistory(device);
  };
  const closeDrawer = (): void => {
    setHistory(undefined);
    setRevoking(undefined);
    setRevokeReason('');
  };

  const rowActions = (d: DeviceView): ReactElement => {
    const a = deviceActions(d, authority.mayManage);
    return (
      <Row gap={2} wrap align="center">
        {a.canIssueCode && <Button variant="secondary" disabled={busy} onClick={() => void issue(d)}>{t('devices.action.issue')}</Button>}
        {a.canRevoke && <Button variant="ghost" disabled={busy} onClick={() => startRevoke(d)}>{t('devices.action.revoke')}</Button>}
      </Row>
    );
  };

  return (
    <Stack gap={5}>
      <PageHeader title={t('devices.title')} subtitle={t('devices.subtitle')} />

      {!authority.mayManage && <Notice tone="neutral">{t('devices.readOnly')}</Notice>}

      {/* D647: an operations-room TV is a Painel TV with its own link now; the kind is no longer offered here. */}
      <Row gap={3} align="center" wrap>
        <Text role="caption" tone="secondary">{t('devices.tvPointer')}</Text>
        <Button variant="ghost" onClick={() => navigate('tv_panels')}>{t('devices.tvPointer.open')}</Button>
      </Row>

      {showPicker && (
        <Panel>
          <Measure size="sm">
            <Field label={t('w6.market.field')} htmlFor="devices-market" hint={t('w6.market.hint')}>
              <Select id="devices-market" value={market ?? ''} onChange={(e) => setPicked(e.target.value === '' ? undefined : e.target.value)}>
                <option value="">{t('w6.market.choose')}</option>
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </Field>
          </Measure>
        </Panel>
      )}

      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {notice !== undefined && history === undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          {devices !== undefined && devices.length > 0 && <Text role="caption" tone="secondary" as="p" className="tnum">{t('devices.summary', counts)}</Text>}

          {issued !== undefined && (
            <Panel>
              <SectionTitle>{t('devices.code.title', { value: issued.device.label })}</SectionTitle>
              <Measure size="md" gap={3}>
                <Text as="p">{t('devices.code.how')}</Text>
                <Text role="display" mono as="p" aria-live="polite">{issued.code}</Text>
                <Text role="caption" tone="secondary" as="p">{t('devices.code.once', { value: instant(issued.expiresAt) })}</Text>
                <Row gap={3}>
                  <Button variant="secondary" onClick={() => setIssued(undefined)}>{t('devices.code.close')}</Button>
                </Row>
              </Measure>
            </Panel>
          )}

          <ListTable
            columns={columns}
            rows={rows}
            rowKey={(d) => d.deviceId}
            onRowClick={(d) => void openHistory(d)}
            {...(history !== undefined ? { selectedKey: history.device.deviceId } : {})}
            actions={rowActions}
            actionsWidth="max-content"
            loading={devices === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('devices.empty.failed') : undefined}
            onRetry={() => void load()}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('devices.empty.title')} detail={t('devices.empty.detail')} />}
            minWidth={1100}
          />

          <Drawer open={history !== undefined} onClose={closeDrawer} labelledBy="devices-history-title">
            {history !== undefined && (
              <>
                <DrawerHeader titleId="devices-history-title" title={t('devices.history.title', { value: history.device.label })} subtitle={<Badge tone={deviceStateTone(history.device.state)}>{v('device.state', history.device.state)}</Badge>} onClose={closeDrawer} closeLabel={t('ui.close')} />
                <DrawerBody>
                  <DrawerStack>
                    {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
                    {revoking !== undefined && revoking.deviceId === history.device.deviceId && (
                      <Stack gap={3}>
                        <SectionTitle>{t('devices.revoke.title', { value: revoking.label })}</SectionTitle>
                        <Notice tone="danger">{t('devices.revoke.warn')}</Notice>
                        <Field label={t('devices.revoke.reason')} htmlFor="devices-revoke-reason">
                          <Select id="devices-revoke-reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)}>
                            <option value="">{t('devices.revoke.choose')}</option>
                            {DEVICE_REVOKE_REASON_CHOICES.map((r) => (
                              <option key={r} value={r}>{v('device.revoke_reason', r)}</option>
                            ))}
                          </Select>
                        </Field>
                      </Stack>
                    )}
                    <HistoryTimeline
                      entries={historyEntries(history)}
                      error={historyError ? t('devices.history.failed') : undefined}
                      onRetry={() => void openHistory(history.device)}
                      emptyTitle={t('devices.history.empty')}
                    />
                    {deviceActions(history.device, authority.mayManage).canDelete && (
                      <Stack gap={2}>
                        <SectionTitle>{t('devices.delete.title')}</SectionTitle>
                        <Text role="caption" tone="secondary" as="p">{t('devices.delete.caption')}</Text>
                        <Row gap={3}>
                          <Button variant="danger" disabled={busy} onClick={() => void remove(history.device)}>{t('devices.delete.button')}</Button>
                        </Row>
                      </Stack>
                    )}
                  </DrawerStack>
                </DrawerBody>
                <DrawerFooter>
                  {revoking !== undefined && revoking.deviceId === history.device.deviceId ? (
                    <>
                      <Button variant="ghost" disabled={busy} onClick={() => { setRevoking(undefined); setRevokeReason(''); }}>{t('ui.cancel')}</Button>
                      <Button variant="danger" disabled={busy || revokeReason === ''} onClick={() => void revoke(revoking)}>{t('devices.revoke.confirm')}</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" onClick={closeDrawer}>{t('devices.history.close')}</Button>
                      {deviceActions(history.device, authority.mayManage).canRevoke && (
                        <Button variant="secondary" disabled={busy} onClick={() => { setRevoking(history.device); setRevokeReason(''); setNotice(undefined); }}>{t('devices.action.revoke')}</Button>
                      )}
                    </>
                  )}
                </DrawerFooter>
              </>
            )}
          </Drawer>

          {authority.mayManage && (
            <Panel>
              <SectionTitle>{t('devices.register.title')}</SectionTitle>
              <Text role="caption" tone="secondary" as="p">{t('devices.register.next')}</Text>
              <Measure size="lg" gap={4}>
                <Field label={t('devices.register.kind')} htmlFor="devices-kind">
                  <Select id="devices-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                    <option value="">{t('devices.register.kindChoose')}</option>
                    {REGISTERABLE_DEVICE_KINDS.map((k) => (
                      <option key={k} value={k}>{v('device.kind', k)}</option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('devices.register.label')} htmlFor="devices-label" hint={t('devices.register.labelHint')}>
                  <TextInput id="devices-label" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
                </Field>
                <Row gap={3}>
                  <Button variant="primary" disabled={busy || kind === '' || label.trim() === ''} onClick={() => void register()}>{t('devices.register.submit')}</Button>
                </Row>
              </Measure>
            </Panel>
          )}
        </>
      )}
    </Stack>
  );
}
